// HRMS-DEV/src/pages/finance/FinanceReimbursements.jsx
// Finance Module — Travel & Reimbursement Review & Approval Page.
//
// Connected Workflow:
// 1. Reads all employee submitted claims in real-time from "reimbursements" collection.
// 2. Displays full claim breakdown (Transportation, Accommodation, Meals, Incidentals, Advance, Net Payable).
// 3. Displays all 1–3 attached PDF bills / receipts with clickable "View Bill 1", "View Bill 2", etc.
// 4. Provides Finance users with Approve & Reject (with required reason) actions that update Firestore permanently.
// 5. Status updates are immediately reflected back in the employee's Travel/Reimbursement module.

import React, { useState, useEffect, useMemo } from "react";
import Layout from "../../components/common/Layout";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  extractClaimDocuments,
} from "../../utils/reimbursementUtils";
import PdfViewerModal from "../../components/common/PdfViewerModal";

/* ── DESIGN TOKENS ───────────────────────────────────────────────────────── */
const PRIMARY = "#164231";
const PRIMARY_LIGHT = "#EAF2EE";
const TEXT = "#1E293B";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const WHITE = "#FFFFFF";
const ERROR = "#DC2626";
const GREEN = "#047857";
const AMBER = "#B45309";
const AMBER_BG = "#FEF3E2";
const BLUE = "#2563EB";
const BLUE_BG = "#E9F1FE";
const SHADOW = "0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)";

/* ── ICONS ───────────────────────────────────────────────────────────────── */
const Icon = {
  Shield: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </svg>
  ),
  BadgeCheck: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 2 9.5 4.5 6 4l-.5 3.5L2 9l2 3-2 3 3.5 1.5L6 20l3.5-.5L12 22l2.5-2.5L18 20l.5-3.5L22 15l-2-3 2-3-3.5-1.5L18 4l-3.5.5L12 2Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  AlertOctagon: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86Z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  FileText: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  ),
  Search: (p) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Eye: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Wallet: (p) => (
    <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.18" {...p}>
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  ),
};

const styles = {
  page: { fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif", padding: "24px", background: "#F7F8FA" },
  wrap: { maxWidth: 1360, width: "100%", margin: "0 auto" },
  headerCard: {
    background: WHITE, borderRadius: 14, marginBottom: 20, boxShadow: SHADOW,
    padding: "24px 30px", display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  title: { fontSize: 24, fontWeight: 800, color: PRIMARY, margin: "0 0 4px" },
  desc: { fontSize: 13.5, color: MUTED, margin: 0 },
  headerIllustration: { width: 80, height: 80, borderRadius: 16, background: PRIMARY_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 },
  statCard: { background: WHITE, borderRadius: 14, boxShadow: SHADOW, padding: "18px 20px", display: "flex", gap: 14, alignItems: "flex-start" },
  statIconBox: (bg, fg) => ({ width: 42, height: 42, borderRadius: 10, background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }),
  statLabel: { fontSize: 12.5, color: MUTED, fontWeight: 600, margin: 0 },
  statValue: { fontSize: 24, fontWeight: 800, color: TEXT, margin: "2px 0 2px" },
  statSub: { fontSize: 11.5, color: MUTED, margin: 0 },

  toolbar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap", justifyContent: "space-between" },
  tabRow: { display: "flex", gap: 8 },
  tab: (active) => ({
    background: active ? PRIMARY : WHITE, border: `1px solid ${active ? PRIMARY : BORDER}`, borderRadius: 8,
    cursor: "pointer", padding: "8px 16px", fontSize: 13, fontWeight: 700, color: active ? WHITE : TEXT,
    transition: "0.15s ease",
  }),
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: WHITE, border: `1px solid ${BORDER}`,
    borderRadius: 8, padding: "8px 14px", minWidth: 260, color: MUTED,
  },
  searchInput: { border: "none", outline: "none", fontSize: 13, flex: 1, color: TEXT, background: "transparent" },

  tableCard: { background: WHITE, borderRadius: 14, boxShadow: SHADOW, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11.5, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "14px 18px", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" },
  td: { padding: "16px 18px", borderBottom: `1px solid ${BORDER}`, fontSize: 13.5, color: TEXT, verticalAlign: "middle" },

  pill: (status) => {
    const s = (status || "").toUpperCase();
    const isApp = s === "APPROVED";
    const isRej = s === "REJECTED";
    return {
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, padding: "4px 11px", borderRadius: 999,
      background: isApp ? "#DCFCE7" : isRej ? "#FEE2E2" : "#FEF3C7",
      color: isApp ? GREEN : isRej ? ERROR : AMBER,
    };
  },
  dot: (status) => {
    const s = (status || "").toUpperCase();
    const isApp = s === "APPROVED";
    const isRej = s === "REJECTED";
    return {
      width: 6, height: 6, borderRadius: "50%",
      background: isApp ? GREEN : isRej ? ERROR : AMBER,
    };
  },

  detailRow: { background: "#F8FAFC" },
  detailWrap: { padding: "20px 22px" },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px 20px",
    marginBottom: 20,
    background: WHITE,
    padding: "16px 18px",
    borderRadius: 10,
    border: `1px solid ${BORDER}`,
  },
  detailLabel: { fontSize: 11, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" },
  detailVal: { fontSize: 13.5, color: TEXT, marginTop: 4, fontWeight: 600, wordBreak: "break-word" },

  actionRow: { display: "flex", gap: 10, marginTop: 16, alignItems: "center", flexWrap: "wrap", paddingTop: 14, borderTop: `1px solid ${BORDER}` },
  approveBtn: { background: GREEN, color: WHITE, border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  rejectBtn: { background: WHITE, color: ERROR, border: `1px solid ${ERROR}`, borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  reasonInput: { flex: 1, minWidth: 240, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "8px 12px", fontSize: 13 },
};

function normalizeStatus(s) {
  const norm = (s || "").toUpperCase();
  if (norm === "APPROVED") return "APPROVED";
  if (norm === "REJECTED") return "REJECTED";
  return "PENDING";
}

function formatSubmitted(ts) {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  if (!d || isNaN(d.getTime())) return { date: "—", time: "" };
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return { date, time };
}

export default function FinanceReimbursements() {
  const { userProfile } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING"); // "PENDING" | "APPROVED" | "REJECTED" | "all"
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actioningId, setActioningId] = useState(null);
  const [msg, setMsg] = useState("");

  // Real-time Firestore listener on "reimbursements" collection
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, "reimbursements"),
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0));
        setClaims(docs);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to reimbursements:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  /* ── APPROVE ACTION ────────────────────────────────────────────────────── */
  const handleApprove = async (id) => {
    setActioningId(id);
    setMsg("");
    try {
      await updateDoc(doc(db, "reimbursements", id), {
        status: "APPROVED",
        reviewedAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        reviewedBy: userProfile?.displayName || userProfile?.email || "Finance Admin",
      });
      setMsg("✅ Claim approved successfully.");
    } catch (err) {
      alert("Error approving claim: " + err.message);
    } finally {
      setActioningId(null);
    }
  };

  /* ── REJECT ACTION ─────────────────────────────────────────────────────── */
  const handleReject = async (id) => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason before rejecting this reimbursement claim.");
      return;
    }
    setActioningId(id);
    setMsg("");
    try {
      await updateDoc(doc(db, "reimbursements", id), {
        status: "REJECTED",
        rejectReason: rejectReason.trim(),
        reviewedAt: serverTimestamp(),
        rejectedAt: serverTimestamp(),
        reviewedBy: userProfile?.displayName || userProfile?.email || "Finance Admin",
      });
      setMsg("✅ Claim rejected. Reason recorded.");
      setRejectingId(null);
      setRejectReason("");
    } catch (err) {
      alert("Error rejecting claim: " + err.message);
    } finally {
      setActioningId(null);
    }
  };

  /* ── DASHBOARD STATS ───────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const pending = claims.filter((c) => normalizeStatus(c.status) === "PENDING").length;
    const approved = claims.filter((c) => normalizeStatus(c.status) === "APPROVED").length;
    const rejected = claims.filter((c) => normalizeStatus(c.status) === "REJECTED").length;
    return { pending, approved, rejected, total: claims.length };
  }, [claims]);

  /* ── FILTERED ROWS ─────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let list = claims.filter((c) => {
      const norm = normalizeStatus(c.status);
      if (statusFilter === "all") return true;
      return norm === statusFilter;
    });

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (c) =>
          (c.emp?.name || c.employeeName || "").toLowerCase().includes(term) ||
          (c.emp?.dept || c.department || "").toLowerCase().includes(term) ||
          (c.emp?.email || "").toLowerCase().includes(term) ||
          (c.id || "").toLowerCase().includes(term)
      );
    }
    return list;
  }, [claims, statusFilter, searchTerm]);

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div style={styles.page}>
        <div style={styles.wrap}>
          {/* Header Banner */}
          <div style={styles.headerCard}>
            <div>
              <h1 style={styles.title}>Travel &amp; Expense Reimbursements</h1>
              <p style={styles.desc}>
                Live queue of employee travel and expense claims. Review itemized details, inspect attached PDF bills, and approve or reject.
              </p>
            </div>
            <div style={styles.headerIllustration}>
              <Icon.Wallet />
            </div>
          </div>

          {msg && (
            <div className="form-success" style={{ marginBottom: 16 }}>
              {msg}
            </div>
          )}

          {/* Stats KPI Cards */}
          <div style={styles.statGrid}>
            <div style={styles.statCard}>
              <div style={styles.statIconBox(AMBER_BG, AMBER)}>
                <Icon.Shield />
              </div>
              <div>
                <p style={styles.statLabel}>Pending Review</p>
                <h3 style={styles.statValue}>{stats.pending}</h3>
                <p style={styles.statSub}>Awaiting Finance action</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIconBox(PRIMARY_LIGHT, GREEN)}>
                <Icon.BadgeCheck />
              </div>
              <div>
                <p style={styles.statLabel}>Approved Claims</p>
                <h3 style={styles.statValue}>{stats.approved}</h3>
                <p style={styles.statSub}>Processed for payment</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIconBox("#FEE2E2", ERROR)}>
                <Icon.AlertOctagon />
              </div>
              <div>
                <p style={styles.statLabel}>Rejected Claims</p>
                <h3 style={styles.statValue}>{stats.rejected}</h3>
                <p style={styles.statSub}>With reason logged</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIconBox(BLUE_BG, BLUE)}>
                <Icon.FileText />
              </div>
              <div>
                <p style={styles.statLabel}>Total Claims</p>
                <h3 style={styles.statValue}>{stats.total}</h3>
                <p style={styles.statSub}>All-time submissions</p>
              </div>
            </div>
          </div>

          {/* Toolbar & Filters */}
          <div style={styles.toolbar}>
            <div style={styles.tabRow}>
              <button style={styles.tab(statusFilter === "PENDING")} onClick={() => setStatusFilter("PENDING")}>
                ⏳ Pending ({stats.pending})
              </button>
              <button style={styles.tab(statusFilter === "APPROVED")} onClick={() => setStatusFilter("APPROVED")}>
                ✓ Approved ({stats.approved})
              </button>
              <button style={styles.tab(statusFilter === "REJECTED")} onClick={() => setStatusFilter("REJECTED")}>
                ✕ Rejected ({stats.rejected})
              </button>
              <button style={styles.tab(statusFilter === "all")} onClick={() => setStatusFilter("all")}>
                All Claims ({stats.total})
              </button>
            </div>

            <div style={styles.searchBox}>
              <Icon.Search />
              <input
                type="text"
                placeholder="Search by employee, dept, or ID…"
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Claims Table */}
          {loading ? (
            <div style={{ background: WHITE, borderRadius: 14, padding: 40, textAlign: "center", color: MUTED }}>
              Loading reimbursement claims…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: WHITE, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
              No claims found under filter "{statusFilter}".
            </div>
          ) : (
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Employee</th>
                    <th style={styles.th}>Department</th>
                    <th style={styles.th}>Date Submitted</th>
                    <th style={styles.th}>Gross Claim</th>
                    <th style={styles.th}>Advance</th>
                    <th style={styles.th}>Net Payable</th>
                    <th style={styles.th}>Attached Bills</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const norm = normalizeStatus(c.status);
                    const isOpen = expandedId === c.id;
                    const { date, time } = formatSubmitted(c.submittedAt || c.date);
                    const empName = c.emp?.name || c.employeeName || "Employee";
                    const empDept = c.emp?.dept || c.department || "—";
                    const netPayable = parseFloat(c.grandTotal ?? c.amount ?? 0);
                    const advanceAmt = parseFloat(c.advance ?? 0);
                    const grossAmt = netPayable + advanceAmt;
                    const docsList = extractClaimDocuments(c);

                    return (
                      <React.Fragment key={c.id}>
                        <tr style={{ cursor: "pointer", background: isOpen ? "#f8fafc" : "transparent" }} onClick={() => setExpandedId(isOpen ? null : c.id)}>
                          <td style={styles.td}>
                            <strong>{empName}</strong>
                            <div style={{ fontSize: 11, color: MUTED }}>{c.emp?.email || `ID: #${c.id.slice(0, 6)}`}</div>
                          </td>
                          <td style={styles.td}>{empDept}</td>
                          <td style={styles.td}>
                            <div>{date}</div>
                            <div style={{ fontSize: 11, color: MUTED }}>{time}</div>
                          </td>
                          <td style={styles.td}>₹{grossAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td style={{ ...styles.td, color: advanceAmt > 0 ? ERROR : MUTED }}>-₹{advanceAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td style={{ ...styles.td, fontWeight: 700, color: PRIMARY }}>₹{netPayable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                            {docsList.length > 0 ? (
                              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {docsList.map((docItem, dIdx) => (
                                  <button
                                    key={dIdx}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewDoc(docItem);
                                    }}
                                    style={{
                                      fontSize: 11,
                                      padding: "3px 8px",
                                      borderRadius: 4,
                                      background: PRIMARY_LIGHT,
                                      color: PRIMARY,
                                      border: "none",
                                      cursor: "pointer",
                                      fontWeight: 600,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                    title={docItem.name || `Bill ${dIdx + 1}`}
                                  >
                                    📄 View PDF {dIdx + 1}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: MUTED }}>None</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <span style={styles.pill(norm)}>
                              <span style={styles.dot(norm)} />
                              {norm}
                            </span>
                          </td>
                          <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn-secondary"
                              style={{ fontSize: 12, padding: "4px 10px" }}
                              onClick={() => setExpandedId(isOpen ? null : c.id)}
                            >
                              {isOpen ? "Hide Details" : "Review"}
                            </button>
                          </td>
                        </tr>

                        {/* Expandable Review & Approval Drawer */}
                        {isOpen && (
                          <tr style={styles.detailRow}>
                            <td colSpan={9} style={{ padding: "16px 20px" }}>
                              <div style={styles.detailWrap}>
                                <div style={styles.detailGrid}>
                                  <div>
                                    <div style={styles.detailLabel}>Official Email</div>
                                    <div style={styles.detailVal}>{c.emp?.email || "—"}</div>
                                  </div>
                                  <div>
                                    <div style={styles.detailLabel}>Contact Phone</div>
                                    <div style={styles.detailVal}>{c.emp?.phone || "—"}</div>
                                  </div>
                                  <div>
                                    <div style={styles.detailLabel}>Transportation Total</div>
                                    <div style={styles.detailVal}>₹{parseFloat(c.transportTotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                                  </div>
                                  <div>
                                    <div style={styles.detailLabel}>Accommodation Total</div>
                                    <div style={styles.detailVal}>₹{parseFloat(c.accomTotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                                  </div>
                                  <div>
                                    <div style={styles.detailLabel}>Meals Total</div>
                                    <div style={styles.detailVal}>₹{parseFloat(c.mealsTotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                                  </div>
                                  <div>
                                    <div style={styles.detailLabel}>Other Incidentals</div>
                                    <div style={styles.detailVal}>₹{parseFloat(c.otherTotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                                  </div>
                                </div>

                                {/* Attached Bills Viewing Section */}
                                <div style={{ marginBottom: 16 }}>
                                  <div style={{ ...styles.detailLabel, marginBottom: 8 }}>Attached PDF Bills &amp; Documents ({docsList.length})</div>
                                  {docsList.length === 0 ? (
                                    <p style={{ fontSize: 13, color: MUTED }}>No bills attached to this claim.</p>
                                  ) : (
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                      {docsList.map((docItem, dIdx) => (
                                        <div
                                          key={dIdx}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            background: WHITE,
                                            border: `1px solid ${BORDER}`,
                                            padding: "6px 12px",
                                            borderRadius: 6,
                                          }}
                                        >
                                          <span style={{ fontSize: 13, fontWeight: 500 }}>
                                            📄 {docItem.name || `Bill ${dIdx + 1}`} {docItem.category ? `(${docItem.category})` : ""}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPreviewDoc(docItem);
                                            }}
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 700,
                                              color: PRIMARY,
                                              padding: "4px 10px",
                                              background: PRIMARY_LIGHT,
                                              borderRadius: 4,
                                              border: "none",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 4,
                                            }}
                                          >
                                            View PDF 👁
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Rejection reason if already rejected */}
                                {norm === "REJECTED" && c.rejectReason && (
                                  <div style={{ background: "#FEE2E2", border: "1px solid #FECACA", padding: "10px 14px", borderRadius: 8, color: ERROR, fontSize: 13, marginBottom: 12 }}>
                                    <strong>Rejection Reason on Record:</strong> {c.rejectReason}
                                    {c.reviewedBy && <span style={{ marginLeft: 8, color: MUTED }}>— by {c.reviewedBy}</span>}
                                  </div>
                                )}

                                {norm === "APPROVED" && (
                                  <div style={{ background: "#DCFCE7", border: "1px solid #BBF7D0", padding: "10px 14px", borderRadius: 8, color: GREEN, fontSize: 13, marginBottom: 12 }}>
                                    <strong>Approved for Reimbursement Payout</strong>
                                    {c.reviewedBy && <span style={{ marginLeft: 8, color: MUTED }}>— by {c.reviewedBy}</span>}
                                  </div>
                                )}

                                {/* Action Buttons for Finance Users */}
                                <div style={styles.actionRow}>
                                  {norm === "PENDING" ? (
                                    <>
                                      <button
                                        style={styles.approveBtn}
                                        onClick={() => handleApprove(c.id)}
                                        disabled={actioningId === c.id}
                                      >
                                        {actioningId === c.id ? "Processing…" : "✓ Approve Claim"}
                                      </button>

                                      {rejectingId === c.id ? (
                                        <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
                                          <input
                                            type="text"
                                            placeholder="Enter rejection reason (required)…"
                                            style={styles.reasonInput}
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                          />
                                          <button
                                            style={{ ...styles.rejectBtn, background: ERROR, color: WHITE }}
                                            onClick={() => handleReject(c.id)}
                                            disabled={actioningId === c.id}
                                          >
                                            Confirm Reject
                                          </button>
                                          <button
                                            className="btn-secondary"
                                            onClick={() => {
                                              setRejectingId(null);
                                              setRejectReason("");
                                            }}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          style={styles.rejectBtn}
                                          onClick={() => setRejectingId(c.id)}
                                          disabled={actioningId === c.id}
                                        >
                                          ✕ Reject Claim
                                        </button>
                                      )}
                                    </>
                                  ) : (
                                    <div style={{ fontSize: 13, color: MUTED }}>
                                      Status locked as <strong>{norm}</strong>. To change, contact Super Admin.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* In-Page PDF & Document Viewer Modal with Close Button */}
      {previewDoc && (
        <PdfViewerModal
          docItem={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </Layout>
  );
}